// hooks/useGasData.ts
import { useRequest } from 'ahooks';
import { buildMonthCycleParams, getDefaultTime, getTimeRangeByMonthPeriod } from '../../utils';
import { Dayjs } from 'dayjs';
import {
  apiV2EnergyReportGasDetailPost,
  apiV2EnergyReportOxygenDetailPost,
  apiV2EnergyReportCh4DetailPost,
  apiV2EnergyReportCo2DetailPost,
  apiV2EnergyReportArgonDetailPost,
} from '@maxtropy/device-customer-apis-v2';
import { DateType } from '../../const';

export enum GasType {
  GAS = 'gas', // 压缩空气
  OXYGEN = 'oxygen', // 氧气
  CH4 = 'ch4', // 天然气
  CO2 = 'co2', // 二氧化碳
  ARGON = 'argon', // 液氩
}

export const OverviewFieldToGasType: Record<string, GasType> = {
  compressedAir: GasType.GAS,
  naturalGas: GasType.CH4,
  oxygen: GasType.OXYGEN,
  argon: GasType.ARGON,
  carbonDioxide: GasType.CO2,
};

export const gasTypeToNumber: Record<GasType, number> = {
  [GasType.GAS]: 4,
  [GasType.OXYGEN]: 5,
  [GasType.CH4]: 6,
  [GasType.CO2]: 7,
  [GasType.ARGON]: 8,
};
export interface GasConfig {
  api: (params: any) => Promise<any>;
  title: string;
}

export type RangeValue = [Dayjs | null, Dayjs | null] | null;

const gasConfigs: Record<GasType, GasConfig> = {
  [GasType.GAS]: {
    api: apiV2EnergyReportGasDetailPost,
    title: '压缩空气',
  },
  [GasType.OXYGEN]: {
    api: apiV2EnergyReportOxygenDetailPost,
    title: '氧气',
  },
  [GasType.CH4]: {
    api: apiV2EnergyReportCh4DetailPost,
    title: '天然气',
  },
  [GasType.CO2]: {
    api: apiV2EnergyReportCo2DetailPost,
    title: '二氧化碳',
  },
  [GasType.ARGON]: {
    api: apiV2EnergyReportArgonDetailPost,
    title: '液氩',
  },
};

export const useGasData = (
  gasType: GasType,
  timeResolution: DateType | undefined,
  value: RangeValue,
  monthPeriodType?: string,
  monthPeriodStart?: number,
  monthPeriodEnd?: number,
  reportScope?: 'factory' | 'dept' | 'area',
  monthCycleApiParams?: {
    deptAreaId?: number;
    monthCycleType: number;
    monthCycleFrom: number;
    monthCycleTo: number;
  }
) => {
  const config = gasConfigs[gasType];

  return useRequest(
    async () => {
      if (!timeResolution || !value?.[0] || !value?.[1]) return null;

      let fromTime: number;
      let toTime: number;

      if (timeResolution === DateType.DAY) {
        // 按日查询：直接使用用户选择的日期范围
        fromTime = value[0].startOf('day').valueOf();
        toTime = value[1].endOf('day').valueOf();
      } else {
        // 按月/按年查询：使用月周期计算
        const timeRange = getTimeRangeByMonthPeriod(
          timeResolution,
          value[0],
          value[1],
          monthPeriodType ?? 'custom',
          monthPeriodStart ?? 26,
          monthPeriodEnd ?? 25
        );
        fromTime = timeRange.startTime;
        toTime = timeRange.endTime;
      }

      return await config.api({
        fromTime,
        toTime,
        timeResolution,
        ...buildMonthCycleParams(timeResolution, monthCycleApiParams, reportScope),
      });
    },
    {
      ready: !!timeResolution && !!value?.[0] && !!value?.[1],
      refreshDeps: [
        gasType,
        timeResolution,
        value,
        monthPeriodType,
        monthPeriodStart,
        monthPeriodEnd,
        reportScope,
        monthCycleApiParams,
      ],
      debounceWait: 100,
    }
  );
};
